// How MANY shops mock mode gives the mock account.
//
// The fixture serves 9 shops. The live account has 43, and the screen's whole
// composition defect only appears at that size: every entry below the shop list
// recedes by ~0.41 of a 390×844 screen per shop added, so at 9 rows the upload
// form and the Advanced disclosure are still on the first screen and the page
// looks fine. Four assessment passes drove this screen at 390px on the 9-row
// fixture and none of them could see it.
//
// That is the sibling of the `REACT_APP_MOCK_FAULTS` lesson one level up: a
// happy-path fixture hides the fault branch, and a SMALL one hides the
// composition. What a fixture can produce decides what can ever be assessed.
//
//   REACT_APP_MOCK_SCALE=43 npm run dev:user     # the live account's size
//   REACT_APP_MOCK_SCALE=0  npm run dev:user     # the empty state
//   REACT_APP_MOCK_SCALE=100 npm run dev:user    # twice the biggest we have
//
// Unset means the fixture as written — 9 shops, every supply state present.
// Scaling is deliberately NOT the default: the 9 are hand-shaped to cover every
// state a row can be in, and a scaled list is repetition, which is the right
// tool for measuring depth and the wrong one for reading states.
//
// Its own module for the same reason `mockFaults` is: `apiService.ts` imports
// `openapi-fetch`, which is ESM-only and untransformed by CRA's jest, so
// anything importing it cannot be unit tested here.

/**
 * `undefined` when unset or unreadable — never a number, so a caller can tell
 * "leave the fixture alone" from "give me zero rows". A negative or
 * non-numeric value is unreadable; zero is a real answer and the empty state
 * is exactly what it is for.
 */
export function parseMockScale(value: string | undefined): number | undefined {
  if (value === undefined || value.trim() === '') return undefined;
  const n = Number(value.trim());
  if (!Number.isInteger(n) || n < 0) return undefined;
  return n;
}

/**
 * Repeat a fixture list up to `count` rows, giving every copy its own identity.
 *
 * IDENTITY IS THE WHOLE JOB. Rows are addressed by `feedId` and keyed by
 * `key`, and a list holding the same key twice renders duplicate React keys and
 * makes every per-row assertion ambiguous. Each copy past the first is
 * re-suffixed, and its `feedId` offset by the pass number × 1000 so it cannot
 * collide with a real one.
 *
 * Truncating below the fixture's own length is honest too: `count` is the
 * answer, not a floor.
 */
export function scaleShops<T extends { key: string; label: string; feedId: number | null }>(
  shops: T[],
  count: number | undefined,
): T[] {
  if (count === undefined || shops.length === 0) return shops;
  if (count <= shops.length) return shops.slice(0, count);

  const out: T[] = [...shops];
  for (let pass = 1; out.length < count; pass++) {
    for (const shop of shops) {
      if (out.length >= count) break;
      out.push({
        ...shop,
        key: `${shop.key}#${pass}`,
        label: `${shop.label} (${pass + 1})`,
        feedId: shop.feedId === null ? null : shop.feedId + pass * 1000,
      });
    }
  }
  return out;
}
